import { createAttractionTranslationsConfig, type AttractionTranslation } from '@/config/objects/attraction-translations';
import { GenerateTranslationVoiceoversDialog, GenerateTranslationVoiceoversIcon } from '@/components/attractions/translations';
import { CrudDetailView, PageContainer, useApiClient } from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * Attraction translation detail page
 */
export function AttractionTranslationDetailPage() {
  const { attractionId, translationId } = useParams<{ attractionId: string; translationId: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [voiceoversDialogOpen, setVoiceoversDialogOpen] = useState(false);
  const [translation, setTranslation] = useState<AttractionTranslation | null>(null);

  const translationsConfig = useMemo(
    () => (attractionId ? createAttractionTranslationsConfig(attractionId) : null),
    [attractionId],
  );

  /**
   * Delete translation mutation
   */
  const deleteMutation = useMutation({
    mutationFn: async (id: string): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/attractions/${attractionId}/translations/${id}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['Translation'] });
        navigate(`/attractions/${attractionId}`);
      } else {
        throw new Error(response.error || 'Failed to delete translation');
      }
    },
  });

  if (!attractionId || !translationId || !translationsConfig) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Translation Not Found</h2>
          <p className="text-muted-foreground">The requested translation could not be found.</p>
        </div>
      </PageContainer>
    );
  }

  /**
   * Navigate to edit form
   */
  const handleEdit = (translation: AttractionTranslation) => {
    navigate(`/attractions/${attractionId}/translations/${translation.id}/edit`);
  };

  /**
   * Handle translation deletion
   */
  const handleDelete = async (translation: AttractionTranslation) => {
    return toast.promise(deleteMutation.mutateAsync(translation.id), {
      loading: 'Deleting translation...',
      success: 'Translation deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete translation',
    });
  };

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Attractions', href: '/attractions' },
    { label: translation?.attraction?.name || 'Attraction', href: `/attractions/${attractionId}` },
    { label: 'Translations', href: `/attractions/${attractionId}` },
    { label: translation?.language?.name || 'Translation', current: true },
  ];

  return (
    <PageContainer>
      <CrudDetailView
        config={translationsConfig}
        itemId={translationId}
        breadcrumbs={breadcrumbs}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onData={(translationData: AttractionTranslation) => setTranslation(translationData)}
        customActions={[
          {
            key: 'generate-voiceovers',
            label: 'Generate Voiceovers',
            icon: GenerateTranslationVoiceoversIcon,
            variant: 'secondary',
            onClick: () => setVoiceoversDialogOpen(true),
          },
        ]}
      />

      {attractionId && translationId && (
        <GenerateTranslationVoiceoversDialog
          attractionId={attractionId}
          translationId={translationId}
          open={voiceoversDialogOpen}
          onOpenChange={setVoiceoversDialogOpen}
        />
      )}
    </PageContainer>
  );
}
